/**
 * The realtime contract.
 *
 * Every consumer of the WebSocket is generated from this file: the API
 * publishes `RealtimeEnvelope`s, the React dashboard subscribes, and the
 * Laravel-served checkout page subscribes to its own session. No consumer ever
 * invents a channel name — it asks the API for a short-lived subscription token
 * naming the channels it may join, which is what stops one merchant's dashboard
 * from watching another's orders.
 *
 * Channel naming is `scope:id:topic`, always. The scope segment is what the
 * authoriser checks; the topic is what a subscriber filters on.
 */
// ─────────────────────────────────────────────────────────────────────────────
// Channels
// ─────────────────────────────────────────────────────────────────────────────
export const RealtimeScope = {
    /** Everything one storefront's dashboard may watch. */
    Shop: 'shop',
    /** Account-level — the wallet balance, shared by all of a merchant's stores. */
    Merchant: 'merchant',
    /** A single checkout, watched by the shopper's own browser. */
    Checkout: 'checkout',
    /** Platform operator only. */
    Platform: 'platform',
};
export const channels = {
    /** New orders and order status changes for one store. */
    shopOrders: (shopId) => `shop:${shopId}:orders`,
    /** Dashboard metric ticks. */
    shopMetrics: (shopId) => `shop:${shopId}:metrics`,
    /** Gateway webhook outcomes as they land. */
    shopPayments: (shopId) => `shop:${shopId}:payments`,
    /** Wallet balance and ledger movement — account-wide, not per store. */
    merchantWallet: (merchantId) => `merchant:${merchantId}:wallet`,
    /** One checkout session, by its public token. The shopper's own browser. */
    checkout: (token) => `checkout:${token}:status`,
    /** Operator dashboard. */
    platformMetrics: () => 'platform:0:metrics',
};
/** Splits `shop:12:orders` into its parts, or returns null if it is not a channel. */
export function parseChannel(channel) {
    const parts = channel.split(':');
    if (parts.length !== 3)
        return null;
    const [scope, id, topic] = parts;
    if (!Object.values(RealtimeScope).includes(scope))
        return null;
    if (!id || !topic)
        return null;
    return { scope: scope, id, topic };
}
// ─────────────────────────────────────────────────────────────────────────────
// Events
// ─────────────────────────────────────────────────────────────────────────────
export const RealtimeEventName = {
    OrderCreated: 'order.created',
    OrderUpdated: 'order.updated',
    OrderCancelled: 'order.cancelled',
    PaymentSucceeded: 'payment.succeeded',
    PaymentFailed: 'payment.failed',
    RefundProcessed: 'refund.processed',
    CheckoutStatusChanged: 'checkout.status_changed',
    CheckoutOtpVerified: 'checkout.otp_verified',
    CheckoutTotalsChanged: 'checkout.totals_changed',
    MetricsTick: 'metrics.tick',
    WalletChanged: 'wallet.changed',
    WalletLow: 'wallet.low',
};
/** Server close codes. The browser client uses these to decide whether to retry. */
export const CloseCode = {
    /** Auth frame never arrived, or the token was rejected. Do not retry blindly. */
    Unauthorized: 4401,
    /** The subscription token expired mid-connection. Fetch a new one and retry. */
    TokenExpired: 4403,
    /** Too many frames. Back off. */
    RateLimited: 4429,
    /** Server going away for a deploy. Retry with jitter. */
    Restarting: 4503,
};
//# sourceMappingURL=realtime.js.map